import { useState, useRef } from "react";
import { UploadCloud, FileText, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiClient } from "@/App";
import { toast } from "sonner";

/**
 * Compact upload trigger — sebuah tombol icon+label yang setara dengan
 * "+ Tambah Baris". Klik → membuka dialog berisi dropzone + tanggal +
 * tombol Upload & Ekstrak. Setelah upload sukses, dialog otomatis ditutup.
 */
export default function UploadCard({ defaultDate, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [planDate, setPlanDate] = useState(defaultDate);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Selaraskan planDate saat dialog dibuka.
  const handleOpenChange = (next) => {
    if (next) {
      setPlanDate(defaultDate);
      setFile(null);
    }
    setOpen(next);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Pilih file PDF dulu");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("plan_date", planDate);
    try {
      const { data } = await apiClient.post("/shunting-plans/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      onUploaded?.(data.plan);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      setOpen(false);
      toast.success(`Berhasil ekstrak ${data.plan?.rows?.length ?? 0} baris`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload gagal";
      toast.error(String(msg));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="rounded-full bg-[#034694] hover:bg-[#02356e] text-white font-display font-semibold"
          data-testid="open-upload-dialog"
          title="Upload & Ekstrak PDF Shunting Plan"
        >
          <UploadCloud className="h-4 w-4 mr-2" /> Upload PDF
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-[#0d1f30] border-[#243a52] text-white max-w-lg"
        data-testid="upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-[#289643]" /> Upload PDF Shunting Plan
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Diproses lokal via pdfplumber — filter otomatis baris DAL (keluar &amp; masuk depo).
          </DialogDescription>
        </DialogHeader>

        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f && f.type === "application/pdf") setFile(f);
            else toast.error("File harus PDF");
          }}
          className={`border border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors duration-150 ${
            dragOver
              ? "border-[#289643] bg-[#289643]/5"
              : "border-[#243a52] hover:border-[#34547a]"
          }`}
          data-testid="dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file ? (
            <>
              <FileText className="h-8 w-8 text-[#289643] mb-2" />
              <div className="text-sm font-mono break-all">{file.name}</div>
              <div className="text-xs text-slate-400 mt-1">
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <button
                type="button"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  setFile(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#CA2047] flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Ganti file
              </button>
            </>
          ) : (
            <>
              <UploadCloud className="h-8 w-8 text-slate-500 mb-2" />
              <div className="text-sm text-slate-300">
                Drop PDF di sini atau klik untuk pilih
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Maks. beberapa MB
              </div>
            </>
          )}
        </label>

        <div className="mt-4 flex items-center gap-3">
          <div className="flex items-center gap-2 border border-[#243a52] rounded-full px-3 py-1.5 bg-[#0c1b2a]">
            <Label className="text-[10px] uppercase tracking-widest text-slate-400">
              Untuk
            </Label>
            <input
              type="date"
              data-testid="upload-date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              className="bg-transparent text-xs font-mono outline-none text-white"
            />
          </div>
          <Button
            onClick={upload}
            disabled={!file || uploading}
            data-testid="upload-submit"
            className="flex-1 rounded-full bg-[#289643] hover:bg-[#1f7a35] disabled:opacity-50 text-white font-display font-semibold"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ekstrak…
              </>
            ) : (
              "Upload & Ekstrak"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
